import React, { useState, useEffect } from "react";
import { Box, IconButton, Paper, Drawer, Tooltip } from "@mui/material";
import {
  DoubleArrow,
  Edit,
  FileCopy,
  OpenInFull,
  CloseFullscreenSharp,
  Delete,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { tooltipStyleProps } from "services/constants/Styles";
import { themes } from "services/constants";
import { customGetAPI } from "services/utils/CustomAPI";
import { EditShiftRota } from "./EditShiftRota";
import { CloneShiftRota } from "./CloneShiftRota";
import { ShiftPreview } from "./ShiftPreview";
import { Loader } from "components/Loader";
import { useForm } from "react-hook-form";
import { useShiftRota } from "../ShiftRotaContext";
import { showToast } from "services/utils/Status";
import { customAPI } from "services/utils/CustomAPI";

const getAutoShiftAssignBasedOnValue = (value) => {
  if (value === "Shift Start Time") return "shift_start_time";
  if (value === "Shift Full Time") return "shift_full_time";
  return value || "";
};

export const ViewShiftRotaPanel = ({
  onClose,
  reCallApi,
  setIsViewPanelOpen,
  showFullPage,
  setShowFullPage,
  setOpenSidebar,
  setHandleLoader,
  loader,
  openSidePeak,
  setOpenSidePeak,
  screenAccess,
}) => {
  const { setValue, watch } = useForm({
    defaultValues: {
      page_expanded: false,
      openSidePeak: false,
    },
  });
  const isEmpty = (obj) => !obj || Object.keys(obj).length === 0;
  const { domain } = useSelector((state) => state.tokenReducer);
  const [getRepresentation, setGetRepresentation] = useState(true);
  const [loading, setLoading] = useState(false);

  const {
    setActiveStep,
    setRepresentationWeekdays,
    setRepresentationShifts,
    setShiftRotationDetails,
    setRepresentationNonRandom,
    editSftRotaData,
    formData,
    setFormData,
    expandPage,
    setExpandPage,
    isEditPanelOpen,
    setIsEditPanelOpen,
    dataUpdated,
    setDataUpdated,
    weekDayRepresentation,
    setWeekDayRepresentation,
    shiftRepresentation,
    setShiftRepresentation,
    rotationDetails,
    setRotationDetails,
    randonRepresentation,
    setRandomRepresentation,
    setIsDateModified,
    setEditSftRotaData,
  } = useShiftRota();

  const handleEditShift = () => {
    if (watch("page_expanded") || showFullPage) {
      setExpandPage(true);
      setOpenSidePeak(false);
      setShowFullPage(true);
    } else {
      setOpenSidePeak(true);
      setValue("page_expanded", false);
    }
    setIsEditPanelOpen(true);
  };

  const handleCloneShift = () => {
    if (watch("page_expanded") || showFullPage) {
      setExpandPage(true);
      setOpenSidePeak(false);
      setShowFullPage(true);
    } else {
      setOpenSidePeak(true);
      setValue("page_expanded", false);
    }
    setIsEditPanelOpen(false);
  };

  const handleEditClosePanel = (openFull) => {
    if (openFull === "openInFullPage") {
      setValue("page_expanded", true);
      setExpandPage(true);
      setOpenSidePeak(false);
      setOpenSidebar(false);
      setShowFullPage(true);
      setIsViewPanelOpen(true);
      setIsEditPanelOpen(isEditPanelOpen);
    } else {
      setOpenSidebar(true);
      setOpenSidePeak(true);
      setIsEditPanelOpen(isEditPanelOpen);
      setShowFullPage(false);
      setExpandPage(false);
    }
  };

  const closePanel = () => {
    setOpenSidePeak(false);
    setExpandPage(false);
    setIsEditPanelOpen(false);
    setGetRepresentation(true);
    setIsDateModified(false);
  };

  useEffect(() => {
    const getShiftRepresentation = async () => {
      setLoading(true);
      if (
        editSftRotaData?.rotation_based_on === "Weekdays" &&
        editSftRotaData?.number_of_shifts > 1
      ) {
        const res = await customGetAPI(
          `${domain}administration/shiftroster/shiftrota/weekly/shift-assignment/representaion/?shift_assignment_id=${editSftRotaData?.id}`,
        );
        if (res.ok) {
          setWeekDayRepresentation(res?.responseDetails);
          setLoading(false);
          setDataUpdated(false);
        } else {
          setLoading(false);
        }
      } else if (
        editSftRotaData?.rotation_based_on === "Shift" &&
        editSftRotaData?.number_of_shifts > 1
      ) {
        const res = await customGetAPI(
          `${domain}administration/shiftroster/shiftrota/shift-assignment-representation/?shift_assignment_id=${editSftRotaData?.id}`,
        );
        if (res.ok) {
          setShiftRepresentation(res?.responseDetails?.cycles);
          setRotationDetails(res?.responseDetails);
          setDataUpdated(false);
          setLoading(false);
        } else {
          setLoading(false);
        }
      } else {
        const res = await customGetAPI(
          `${domain}administration/shiftroster/shiftrota/random/shift-assignment/representaion/?shift_assignment_id=${editSftRotaData?.id}`,
        );
        if (res.ok) {
          setRandomRepresentation(res?.responseDetails);
          setLoading(false);
          setDataUpdated(false);
        } else {
          setLoading(false);
        }
      }
    };
    if (editSftRotaData?.id && getRepresentation && dataUpdated) {
      setGetRepresentation(false);
      getShiftRepresentation();
    }
  }, [
    dataUpdated,
    domain,
    editSftRotaData?.id,
    editSftRotaData?.number_of_shifts,
    editSftRotaData?.rotation_based_on,
    editSftRotaData.shift_type,
    formData.schedule.callRepresentationApi,
    formData.schedule.rotation_based_on,
    formData.schedule.sft_assignment_id,
    getRepresentation,
    setDataUpdated,
    setEditSftRotaData,
    setFormData,
    setRandomRepresentation,
    setRepresentationNonRandom,
    setRepresentationShifts,
    setRepresentationWeekdays,
    setRotationDetails,
    setShiftRepresentation,
    setShiftRotationDetails,
    setWeekDayRepresentation,
  ]);

  const renderComponent = () => {
    return isEditPanelOpen ? (
      <EditShiftRota
        closeEditPanel={closePanel}
        onEditClosePanel={handleEditClosePanel}
        reCallApi={reCallApi}
        setOpenSidebar={setOpenSidebar}
        setHandleLoader={setHandleLoader}
        loader={loader}
        setShowFullPage={setShowFullPage}
      />
    ) : (
      <CloneShiftRota
        onClose={onClose}
        closeClonePanel={closePanel}
        onEditClosePanel={handleEditClosePanel}
        reCallApi={reCallApi}
        setOpenSidebar={setOpenSidebar}
        setHandleLoader={setHandleLoader}
        loader={loader}
        editSftRotaData={editSftRotaData}
        setOpenSidePeak={setOpenSidePeak}
      />
    );
  };

  const handleDeleteSftRota = async () => {
    const baseUrl = `${domain}administration/shiftroster/shiftrota`;

    let endpoint = "";
    let payload = {};

    const safeReverseDate = (date) =>
      date ? date.split("-").reverse().join("-") : null;

    const commonPayload = {
      name: editSftRotaData?.name.trim(),
      rotation_based_on: editSftRotaData?.rotation_based_on,
      shift_groups: editSftRotaData?.shift_groups,
      shifts: editSftRotaData?.shifts,
      rotation_frequency: editSftRotaData?.rotation_frequency,
      rotation_day: editSftRotaData?.rotation_day,
      no_of_repetition: editSftRotaData?.no_of_repetition,
      start_date: safeReverseDate(editSftRotaData?.start_date),
      auto_shift_assign: editSftRotaData?.auto_shift_assign,
      auto_shift_assign_based_on: getAutoShiftAssignBasedOnValue(
        editSftRotaData?.auto_shift_assign_based_on,
      ),
      next_shift_start_buffer: editSftRotaData?.next_shift_start_buffer,
      first_rotation_date: safeReverseDate(
        editSftRotaData?.first_rotation_date,
      ),
      cycles: editSftRotaData?.cycles,
      number_of_shifts: editSftRotaData?.no_of_shifts,
      shift_type: editSftRotaData?.shift_type,
      isdeleted: true,
    };

    const otherPayload = {
      name: editSftRotaData?.name?.trim(),
      shift_groups: editSftRotaData?.shift_groups,
      shifts: editSftRotaData?.shifts,
      start_date: safeReverseDate(editSftRotaData?.start_date),
      number_of_shifts: editSftRotaData?.no_of_shifts,
      shift_type: editSftRotaData?.shift_type,
      cycles: [],
      isdeleted: true,
    };

    switch (editSftRotaData?.rotation_based_on) {
      case "Shift":
        endpoint = `${baseUrl}/shift-assignment-based-shift/${editSftRotaData?.id}/`;
        payload = commonPayload;
        break;

      case "Weekdays":
        endpoint = `${baseUrl}/shift-assignments/${editSftRotaData?.id}/`;
        payload = commonPayload;
        break;

      default:
        endpoint = `${baseUrl}/shift-assignment-based-shift/${editSftRotaData?.id}/`;
        payload = otherPayload;
        break;
    }

    const response = await customAPI(endpoint, "PUT", payload);

    if (response.ok) {
      showToast("success", `Shift rota details deleted.`);
      reCallApi(true);
      onClose();
    } else {
      showToast("error", "Failed to delete rota details");
    }
  };

  return loading ? (
    <Loader />
  ) : (
    <>
      {!expandPage && !isEditPanelOpen ? (
        <Box
          sx={{
            height: "100vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: 0,
            }}
          >
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {!showFullPage ? (
                    <>
                      <Box sx={{ ml: 1 }}>
                        <IconButton
                          onClick={onClose}
                          sx={{
                            color: themes?.grayColor,
                            "&:hover": {
                              backgroundColor: themes?.whiteColor,
                            },
                          }}
                        >
                          <DoubleArrow />
                        </IconButton>
                      </Box>
                      <Tooltip
                        title="Open in full page"
                        arrow
                        slotProps={tooltipStyleProps}
                      >
                        <Box>
                          <IconButton
                            onClick={() => {
                              setShowFullPage(true);
                              setOpenSidebar(false);
                            }}
                            sx={{
                              color: themes?.grayColor,
                              "&:hover": {
                                backgroundColor: themes?.whiteColor,
                              },
                            }}
                          >
                            <OpenInFull />
                          </IconButton>
                        </Box>
                      </Tooltip>
                    </>
                  ) : (
                    <Box sx={{ ml: 0.5 }}>
                      <Tooltip
                        title="Back to side panel"
                        arrow
                        slotProps={tooltipStyleProps}
                      >
                        <IconButton
                          onClick={() => {
                            setShowFullPage(false);
                            setOpenSidebar(true);
                          }}
                          sx={{
                            color: themes?.grayColor,
                            "&:hover": {
                              backgroundColor: themes?.whiteColor,
                            },
                          }}
                        >
                          <CloseFullscreenSharp />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      borderRadius: "50%",
                      padding: "4px 4px",
                      gap: 0.5,
                    }}
                  >
                    {editSftRotaData?.is_editable && (
                      <Tooltip
                        title="Delete"
                        arrow
                        slotProps={tooltipStyleProps}
                      >
                        <IconButton
                          onClick={() => {
                            if (screenAccess) {
                              handleDeleteSftRota();
                            } else {
                              showToast(
                                "error",
                                `Access denied: You are not authorized to delete shift rota`,
                              );
                            }
                          }}
                          sx={{
                            color: themes?.redColor,
                            backgroundColor: themes?.primary,
                            borderRadius: "50%",
                            "&:hover": { backgroundColor: themes?.primary },
                            mt: 0.5,
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Clone" arrow slotProps={tooltipStyleProps}>
                      <IconButton
                        onClick={() => {
                          if (screenAccess) {
                            setActiveStep(0);
                            handleCloneShift();
                            setValue("is_clone", true);
                          } else {
                            showToast(
                              "error",
                              `Access denied: You are not authorized to clone shift rota`,
                            );
                          }
                        }}
                        sx={{
                          color: themes?.blueColor,
                          backgroundColor: themes?.primary,
                          borderRadius: "50%",
                          "&:hover": { backgroundColor: themes?.primary },
                        }}
                      >
                        <FileCopy />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit" arrow slotProps={tooltipStyleProps}>
                      <IconButton
                        onClick={() => {
                          if (screenAccess) {
                            setActiveStep(0);
                            handleEditShift();
                            setValue("is_clone", false);
                          } else {
                            showToast(
                              "error",
                              `Access denied: You are not authorized to edit shift rota`,
                            );
                          }
                        }}
                        sx={{
                          color: themes?.greenColor,
                          backgroundColor: themes?.primary,
                          borderRadius: "50%",
                          "&:hover": { backgroundColor: themes?.primary },
                        }}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
              {!isEmpty(formData?.config) && !isEmpty(formData?.schedule) && (
                <Box>
                  {editSftRotaData?.length !== 0 && (
                    <ShiftPreview
                      representationWeekdays={weekDayRepresentation}
                      representationShifts={shiftRepresentation}
                      editSftRotaData={editSftRotaData}
                      shiftRotationDetails={rotationDetails}
                      representationNonRandon={randonRepresentation}
                    />
                  )}
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      ) : null}
      <Drawer
        anchor="right"
        open={openSidePeak}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          closePanel();
        }}
        sx={{
          "& .MuiDrawer-paper": {
            width: "680px",
            maxWidth: "90vw",
            backgroundColor: themes?.whiteColor,
          },
        }}
      >
        {renderComponent()}
      </Drawer>

      {expandPage ? (
        <Box
          sx={{
            backgroundColor: themes?.whiteColor,
          }}
        >
          {renderComponent()}
        </Box>
      ) : null}
    </>
  );
};
